import React, { useState, useEffect, useCallback } from "react";
import { Button, Alert } from "react-bootstrap";
import CollapsibleSection from "./CollapsibleSection";
import ExpandableTxTable, { TxColumn } from "./ExpandableTxTable";
import AddCreditModal from "./AddCreditModal";
import AdjustCreditModal from "./AdjustCreditModal";
import RefundCreditModal from "./RefundCreditModal";
import EnrollmentService from "../../../_services/EnrollmentService";
import { fmtAmount } from "./format";

/**
 * Colour tokens for each credit-account transaction type. Uses theme tokens so
 * the badges adapt to dark mode. CreditTransactionType: 1=Added, 2=Reversed,
 * 3=Adjusted, 4=Refund.
 */
const txTypeStyle = (type: number): React.CSSProperties => {
  const map: Record<number, { bg: string; fg: string }> = {
    1: { bg: "var(--status-success-bg)", fg: "var(--status-success)" },
    3: { bg: "var(--status-info-bg)", fg: "var(--status-info)" },
    4: { bg: "var(--status-warning-bg)", fg: "var(--status-warning)" },
  };
  const c = map[type] || { bg: "var(--status-danger-bg)", fg: "var(--status-danger)" };
  return {
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 600,
    background: c.bg,
    color: c.fg,
  };
};

interface ExcessPaymentsSectionProps {
  enrollmentId: number;
}

/**
 * Excess Payments — mirrors the Java "Excess Amount Log" block. Excess /
 * overpayment lives in the student credit account, so this block shows the
 * credit-account ledger (balance + transactions) and keeps the Add / Adjust /
 * Refund credit actions. Replaces the former standalone Credit Account block.
 */
const ExcessPaymentsSection: React.FC<ExcessPaymentsSectionProps> = ({
  enrollmentId,
}) => {
  const [loading, setLoading] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [ledger, setLedger] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  const loadCreditAccount = useCallback(() => {
    setLoading(true);
    setError(null);
    EnrollmentService.getCreditAccount(enrollmentId)
      .then((res: any) => {
        if (res.data?.success) {
          setCreditBalance(Number(res.data.data.creditBalance) || 0);
          setLedger(res.data.data.ledger || []);
        } else {
          setError(res.data?.message || "Failed to load excess payments");
        }
      })
      .catch((err: any) =>
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load excess payments",
        ),
      )
      .finally(() => setLoading(false));
  }, [enrollmentId]);

  useEffect(() => {
    loadCreditAccount();
  }, [loadCreditAccount]);

  const columns: TxColumn[] = [
    { header: "Date", cell: (e) => e.transactionDate || "--", nowrap: true },
    {
      header: "Transaction Type",
      cell: (e) => (
        <span style={txTypeStyle(e.creditTransactionType)}>
          {e.creditTransactionTypeName}
        </span>
      ),
    },
    {
      header: "Amount",
      align: "right",
      cell: (e) => {
        const amt = Number(e.amount) || 0;
        return (
          <span
            style={{
              fontWeight: 600,
              color: amt >= 0 ? "var(--status-success)" : "var(--status-danger)",
            }}
          >
            {amt >= 0 ? "+" : ""}
            {fmtAmount(amt)}
          </span>
        );
      },
    },
    { header: "Reference", cell: (e) => e.description || "--" },
    { header: "Credit Type", cell: (e) => e.creditTypeName || "--" },
  ];

  return (
    <>
      <CollapsibleSection
        title="Excess Payments"
        count={ledger.length}
        defaultOpen={true}
      >
        {loading ? (
          <div className="ev-loading">
            <i className="fa fa-spinner fa-spin" /> Loading excess payments...
          </div>
        ) : error ? (
          <Alert variant="danger" style={{ fontSize: 12, marginBottom: 0 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Balance bar with actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 14 }}>
                Available Excess / Credit:{" "}
                <span
                  style={{
                    color:
                      creditBalance > 0
                        ? "var(--status-success)"
                        : "var(--text-muted)",
                  }}
                >
                  {fmtAmount(creditBalance)}
                </span>
              </span>
              <div>
                <Button
                  variant="primary"
                  size="sm"
                  style={{ fontSize: 11, padding: "2px 10px" }}
                  onClick={() => setShowAddModal(true)}
                >
                  <i className="fa fa-plus" /> Add Credit
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ fontSize: 11, padding: "2px 10px", marginLeft: 6 }}
                  onClick={() => setShowAdjustModal(true)}
                  disabled={creditBalance <= 0}
                >
                  <i className="fa fa-exchange" /> Adjust Credit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  style={{ fontSize: 11, padding: "2px 10px", marginLeft: 6 }}
                  onClick={() => setShowRefundModal(true)}
                  disabled={creditBalance <= 0}
                >
                  <i className="fa fa-undo" /> Refund Credit
                </Button>
              </div>
            </div>

            {/* Ledger table */}
            {ledger.length === 0 ? (
              <div className="ev-empty">No excess payments</div>
            ) : (
              <ExpandableTxTable
                rows={ledger}
                columns={columns}
                hasDetails={(e) => !!(e.remarks || e.createdByName)}
                renderDetails={(e) => (
                  <div className="ev-detail-meta" style={{ borderTop: "none", marginTop: 0, paddingTop: 0 }}>
                    {e.createdByName && (
                      <span>
                        Created By: <b>{e.createdByName}</b>
                      </span>
                    )}
                    {e.remarks && (
                      <span>
                        {e.createdByName ? " · " : ""}Remarks: <b>{e.remarks}</b>
                      </span>
                    )}
                  </div>
                )}
              />
            )}
          </>
        )}
      </CollapsibleSection>

      {showAddModal && (
        <AddCreditModal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          enrollmentId={enrollmentId}
          onSuccess={() => {
            setShowAddModal(false);
            loadCreditAccount();
          }}
        />
      )}

      {showAdjustModal && (
        <AdjustCreditModal
          show={showAdjustModal}
          onHide={() => setShowAdjustModal(false)}
          enrollmentId={enrollmentId}
          availableCredit={creditBalance}
          onSuccess={() => {
            setShowAdjustModal(false);
            loadCreditAccount();
          }}
        />
      )}

      {showRefundModal && (
        <RefundCreditModal
          show={showRefundModal}
          onHide={() => setShowRefundModal(false)}
          enrollmentId={enrollmentId}
          availableCredit={creditBalance}
          onSuccess={() => {
            setShowRefundModal(false);
            loadCreditAccount();
          }}
        />
      )}
    </>
  );
};

export default ExcessPaymentsSection;
